/*eslint complexity: ["error", 72]*/
import React from 'react';
import type { TFunction } from 'i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { GetSalesInvoiceDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  InvoiceTypeEnum,
  InvoiceTypeObj,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';
import { NewWindowButton } from '../SalesInvoices/NewWindowButton';
import { buildProjectLabel } from '../../../utils/buildProjectLabel';
import { AddEditEmailButton } from '../EmailAddress/AddEmailButton';

import { CustomEmailDataLink } from './CustomEmailDataLink';
import { ChooseEmailButton } from './ChooseEmailButton';
import { EmailAdressCommentInfo } from './EmailAdressCommentInfo';

interface SendFVSByEmailSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => void;
  refresh?: boolean;
  enableMultiSelect?: boolean;
  withAddEmailAddresses?: boolean;
  withoutToCC?: boolean;
  withoutFrom?: boolean;
  selectedIds?: string[];
}

export const sendFVSByEmailSearchGrid = (
  salesInvoice: GetSalesInvoiceDto,
  t: TFunction<'fvsLng', 'fvsLng'>,
  tk: TKeys<'fvsLng'>,
  refresh: boolean,
  setRefresh: (refresh: boolean) => void,
) => {
  return {
    key: salesInvoice.id,
    values: {
      'salesInvoice.systemNumber': salesInvoice.systemNumber,
      'salesInvoice.number': (
        <>
          {salesInvoice.number}
          {salesInvoice.number && <br />}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>{t(InvoiceTypeObj[salesInvoice.type])}</Tooltip>}
          >
            {salesInvoice.type === InvoiceTypeEnum.Foreign ? (
              <Icon.Globe2 className="icon-blue me-2" />
            ) : (
              <Icon.House className="icon-blue me-2" />
            )}
          </OverlayTrigger>
          {!!salesInvoice.parent && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  {t(tk.finance.correctionToInvoice)}{' '}
                  {salesInvoice.originalInvoice?.number}
                </Tooltip>
              }
            >
              <Icon.FileEarmarkMinusFill className="icon-blue me-2" />
            </OverlayTrigger>
          )}
        </>
      ),
      'salesInvoice.invoiceDate': momencik(salesInvoice.invoiceDate),
      'salesInvoice.saleDate': momencik(salesInvoice.saleDate),
      project_label: salesInvoice.salesInvoiceProjects
        ?.map(({ project }) => buildProjectLabel(project))
        .join(', '),
      'payableDays.value': salesInvoice.payableDays?.value,
      'client.name': salesInvoice.purchaser?.client?.name,
      'client.nip': salesInvoice.purchaser?.client?.nip,
      to: (
        <>
          <div className="d-flex align-items-center">
            {salesInvoice.purchaser?.client?.emailAddresses?.length === 1
              ? salesInvoice.purchaser?.client?.emailAddresses[0].emailsTo
              : salesInvoice.emailAddress?.emailsTo}
            <div className="ms-auto d-flex align-items-center">
              {(salesInvoice.emailAddress ||
                salesInvoice.purchaser?.client?.emailAddresses?.length ===
                  1) && (
                <AddEditEmailButton
                  emailAddress={
                    salesInvoice.purchaser?.client?.emailAddresses?.length === 1
                      ? salesInvoice.purchaser?.client?.emailAddresses[0]
                      : salesInvoice.emailAddress
                  }
                  clientId={salesInvoice?.purchaser?.client?.id}
                  setRefresh={setRefresh}
                  refresh={refresh}
                  client={salesInvoice?.purchaser?.client}
                />
              )}
              {salesInvoice?.purchaser?.client?.emailAddresses &&
                salesInvoice?.purchaser?.client?.emailAddresses?.length > 1 && (
                  <ChooseEmailButton
                    clientId={salesInvoice?.purchaser?.client?.id}
                    salesInvoiceId={salesInvoice.id}
                    setRefresh={setRefresh}
                    refresh={refresh}
                  />
                )}
            </div>
          </div>
        </>
      ),
      cc:
        salesInvoice.purchaser?.client?.emailAddresses?.length === 1 ? (
          <>
            {salesInvoice.purchaser?.client?.emailAddresses[0].emailsDw}{' '}
            <EmailAdressCommentInfo
              comment={
                salesInvoice.purchaser?.client?.emailAddresses[0].comment
              }
            />
          </>
        ) : (
          <>
            {salesInvoice.emailAddress?.emailsDw}{' '}
            <EmailAdressCommentInfo
              comment={salesInvoice.emailAddress?.comment}
            />
          </>
        ),
      customEmail: (
        <CustomEmailDataLink
          invoiceId={salesInvoice.id}
          emailText={salesInvoice.emailText}
          emailSubject={salesInvoice.emailSubject}
          hasAttachments={salesInvoice.hasAttachments}
          setRefresh={setRefresh}
          refresh={refresh}
        />
      ),
      emailFrom:
        salesInvoice.purchaser?.client?.emailAddresses?.length === 1 ? (
          <>
            {
              salesInvoice.purchaser?.client?.emailAddresses[0].emailFrom.value
            }{' '}
          </>
        ) : (
          <>{salesInvoice.emailAddress?.emailFrom.value} </>
        ),
      addEmailAddresses: salesInvoice?.purchaser?.client?.id && (
        <AddEditEmailButton
          clientId={salesInvoice?.purchaser?.client?.id}
          setRefresh={setRefresh}
          refresh={refresh}
          client={salesInvoice?.purchaser?.client}
        />
      ),
      newWindowButton: <NewWindowButton salesInvoiceId={salesInvoice.id} />,
    },
  };
};

export const SendFVSByEmailSearch: React.FC<
  SendFVSByEmailSearchProps
> = props => {
  const { t, tk } = useTranslation('fvsLng');

  const columns: GridColumn[] = [
    {
      header: '',
      property: 'newWindowButton',
      noSorting: true,
      noFilter: true,
    },
    { header: t(tk.common.sysNumber), property: 'salesInvoice.systemNumber' },
    { header: t(tk.common.number), property: 'salesInvoice.number' },
    { header: t(tk.common.issueDate), property: 'salesInvoice.invoiceDate' },
    { header: t(tk.common.saleDate), property: 'salesInvoice.saleDate' },
    { header: t(tk.finance.buyerName), property: 'client.name' },
    ...(props.withoutToCC
      ? []
      : [
          {
            header: t(tk.sendEmail.to),
            property: 'to',
            noFilter: true,
            noSorting: true,
          },
          {
            header: t(tk.sendEmail.cc),
            property: 'cc',
            noFilter: true,
            noSorting: true,
          },
        ]),
    ...(props.withoutFrom
      ? []
      : [
          {
            header: t(tk.sendEmail.emailFrom),
            property: 'emailFrom',
            noFilter: true,
            noSorting: true,
          },
        ]),
    {
      header: t(tk.sendEmail.customEmail),
      property: 'customEmail',
      noFilter: true,
      noSorting: true,
    },
    { header: t(tk.prLang.project), property: 'project_label' },
    ...(props.withAddEmailAddresses
      ? [
          {
            header: '',
            property: 'addEmailAddresses',
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="salesInvoice.systemNumber"
      initialOrderDirection="ASC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.finance.noInvoices)}
      withHistory={true}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      enableMultiSelect={props.enableMultiSelect}
      withFilterInColumns={true}
      withOverflowXAuto={true}
      selectedKeys={props.selectedIds}
    />
  );
};
